import { FC, FormEvent } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import styled from "styled-components";

import { FormLayout, Label, Larger } from "@/components/Forms";
import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import { api } from "@/utils/api";
import { ReleaseResource } from "@/types/resources";
import Button from "@/components/Button";
import Text from "@/components/Text";
import { handleFormErrors } from "@/utils/helpers";
import useAlerts from "@/hooks/useAlerts";
import FormField from "@/components/Forms/FormField";
import { ReactComponent as PlusIcon } from "@/assets/icons/plus.svg";
import AudienceSegmentationField from "./AudienceSegmentationField";
import RepostsFieldsArray from "./RepostsFieldsArray";
import { AddButtonHolder } from "@/components/Testing";

const NumberBadge = styled(Text)`
  background: ${({ theme }) => theme.colors.purpleLighter};
  border-radius: 100px;
  padding: 4px 12px;
  margin-right: 10px;
`;

const ConditionHeadline = styled(Larger)`
  display: flex;
  align-items: center;
`;

const RemoveButton = styled(Text).attrs({
  $type: "smallHeadline",
  $color: "error",
  type: "button",
  as: "button",
})`
  margin-left: auto;
  &:hover {
    text-decoration: underline;
  }
`;

const Condition = styled(Larger)`
  &:nth-child(even) {
    position: relative;
    margin: 24px 0;
    &::before {
      position: absolute;
      content: "";
      z-index: 0;
      left: -32px;
      top: -24px;
      width: calc(100% + 32px + 32px);
      height: calc(100% + 24px + 24px);
      background: ${({ theme }) => theme.colors.grayscaleLight};
    }

    > ${FormLayout} {
      position: relative;
    }
  }
`;

export type FormDataT = {
  conditions: Array<{
    views: string;
    period: string;
    male_percentage: string;
    female_percentage: string;
    reposts: Array<{
      channel_id?: number;
      compilation_type?: number;
      date?: string;
    }>;
  }>;
};

interface Props {
  onClose: () => void;
  release: ReleaseResource;
}

const DEFAULT_CONDITION = {
  views: "",
  period: "",
  female_percentage: "",
  male_percentage: "",
  reposts: [{}],
};

const DistributionRepostsForm: FC<Props> = ({ onClose, release }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      conditions: [{ ...DEFAULT_CONDITION }],
    },
  });

  const {
    fields: conditions,
    append: appendCondition,
    remove: removeCondition,
  } = useFieldArray({
    control,
    name: "conditions",
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    return handleSubmit(async (data) => {
      try {
        await api.post("/v1/conditional-reposts", {
          release_id: release.id,
          ...data,
        });

        queryClient.invalidateQueries(["releases"], {
          exact: false,
        });

        alerts.success(`New reposts have been successfully created`);
        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel heading="Add conditional reposts" onClose={onClose}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              {conditions.map((c, index) => (
                <Condition key={c.id}>
                  <FormLayout>
                    <ConditionHeadline>
                      <NumberBadge $type="smallBold" $color="white">
                        Nr. {index + 1}
                      </NumberBadge>
                      <Text $type="caption">Conditional repost</Text>
                      {conditions.length > 1 && (
                        <RemoveButton onClick={() => removeCondition(index)}>
                          Remove
                        </RemoveButton>
                      )}
                    </ConditionHeadline>
                    <FormField<FormDataT>
                      name={`conditions.${index}.views`}
                      control={control}
                      label="Target views"
                      placeholder="Add number"
                    />
                    <FormField<FormDataT>
                      name={`conditions.${index}.period`}
                      control={control}
                      label="Target period"
                      placeholder="Add number"
                    />
                    <Larger>
                      <Label as="p">Audience segmentation</Label>
                      <FormLayout>
                        <AudienceSegmentationField
                          name={`conditions.${index}.male_percentage`}
                          control={control}
                          label="Male % at least:"
                        />
                        <AudienceSegmentationField
                          name={`conditions.${index}.female_percentage`}
                          control={control}
                          label="Female % at least:"
                        />
                      </FormLayout>
                    </Larger>
                    <Larger>
                      <RepostsFieldsArray
                        control={control}
                        conditionsIndex={index}
                        title={release.title}
                        description={release.description ?? ""}
                      />
                    </Larger>
                  </FormLayout>
                </Condition>
              ))}
            </FormLayout>
            <AddButtonHolder>
              <Button
                type="button"
                $type="colorized"
                onClick={() => appendCondition({ ...DEFAULT_CONDITION })}
              >
                <PlusIcon />
                Add conditional repost
              </Button>
            </AddButtonHolder>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Add
            </Button>
            <Button color="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default DistributionRepostsForm;
